import { inject, observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { blue } from "../UI/themes";
import { auth } from "../config/firebase";
import { signOut } from "firebase/auth";
import heroEllipse from "../assets/img/admin-nav/hero-ellipse.svg";

// Admin mobile nav bar (Figma 1459:11434 / 1460:13303). Left: logout + language
// toggle + avatar. Right: title + a back arrow on sub-pages. The dashboard and
// the project-detail (tenants) screens use a tall curved "hero" header with a
// centered page title; the list screens use a flat bar.
const BackArrow = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LogoutIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 21H6a2 2 0 01-2-2V5a2 2 0 012-2h3"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 17l5-5-5-5M21 12H9"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AdminMobileHeader = ({ userStore, tenantsStore }) => {
  const theme = blue;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("admin");
  const location = useLocation().pathname;

  const isMain = location === "/admin/projects"; // dashboard (no back)
  const isListView = location.endsWith("/list");
  // Tall curved header on the dashboard + project-detail (tenants, not its list).
  const isHero = !isListView && (isMain || location.includes("/tenants"));
  const heroTitle = isMain
    ? t("menu:projects")
    : tenantsStore.selectedProject?.SITEDES || "";

  const initials = (userStore.userDetails?.NAME || "")
    .split(" ")
    .map((n) => n.slice(0, 1))
    .join("");

  const toggleLang = () => {
    const next = i18n.language === "he" ? "en" : "he";
    i18n.changeLanguage(next);
    userStore.setLanguage?.(next);
  };

  const logout = () => {
    userStore.logout();
    tenantsStore.setContracts([]);
    signOut(auth);
    navigate("admin/login");
  };

  const iconBtn = {
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
  };

  return (
    <div
      style={{
        position: "relative",
        flexShrink: 0,
        overflow: "hidden",
        backgroundColor: theme.color.primary,
        ...(isHero
          ? {
              borderBottomLeftRadius: 28,
              borderBottomRightRadius: 28,
              // extra purple band below the title so the first card floats over it
              paddingBottom: 52,
            }
          : {}),
      }}
    >
      {/* decorative gradient ellipse (hero only) */}
      {isHero && (
        <img
          src={heroEllipse}
          alt=""
          style={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            width: 300,
            height: 300,
            pointerEvents: "none",
          }}
        />
      )}

      {/* nav row */}
      <div style={{ position: "relative", zIndex: 1, height: 50 }}>
        {/* left group: logout + language toggle + avatar */}
        <div
          style={{
            position: "absolute",
            left: 16,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button type="button" aria-label="logout" style={iconBtn} onClick={logout}>
            <LogoutIcon color={theme.color.white} />
          </button>
          <button
            type="button"
            aria-label="language"
            style={{
              ...iconBtn,
              color: theme.color.white,
              fontFamily: "Rubik",
              fontSize: 14,
              fontWeight: 500,
            }}
            onClick={toggleLang}
          >
            {i18n.language === "he" ? "En" : "עב"}
          </button>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: `1px solid ${theme.color.white}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: theme.color.white,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "Rubik",
              }}
            >
              {initials}
            </span>
          </div>
        </div>

        {/* nav-row title (right) */}
        <div
          style={{
            position: "absolute",
            right: 54,
            left: 140,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme.color.white,
            fontSize: 18,
            fontWeight: 500,
            fontFamily: "Rubik",
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: isMain ? "default" : "pointer",
          }}
          onClick={isMain ? undefined : () => navigate(-1)}
        >
          {isMain ? t("menu:admin") : t("back_to_projects")}
        </div>

        {/* back arrow on sub-pages */}
        {!isMain && (
          <button
            type="button"
            aria-label="back"
            style={{
              ...iconBtn,
              position: "absolute",
              right: 18,
              top: "50%",
              transform: "translateY(-50%)",
            }}
            onClick={() => navigate(-1)}
          >
            <BackArrow color={theme.color.white} />
          </button>
        )}
      </div>

      {/* hero: centered page title below the nav row */}
      {isHero && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            color: theme.color.white,
            fontSize: 18,
            fontWeight: 700,
            fontFamily: "Rubik",
            lineHeight: "30px",
            paddingTop: 6,
          }}
        >
          {heroTitle}
        </div>
      )}
    </div>
  );
};

export default inject("userStore", "tenantsStore")(observer(AdminMobileHeader));
